import { setContext, setTag } from '@pleased-npm/error-tracking';
import type { ChatbotTrackingContext, RouterLike, TrackingRouteLike } from './types';

/** Last known chatbot context — partial updates merge instead of wiping unset keys. */
let chatbotContext: ChatbotTrackingContext = {};

/** Reset chatbot context — tests only (via `resetErrorTrackingForTests`). */
export const resetChatbotContextForTests = (): void => {
  chatbotContext = {};
};

export const getChatbotContext = (): Readonly<ChatbotTrackingContext> => chatbotContext;

const omitUndefined = <T extends Record<string, unknown>>(value: T): Partial<T> =>
  Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as Partial<T>;

/**
 * Apply chatbot context for GlitchTip events.
 * Merges with previously set values so callers can update one field at a time
 * without wiping keys that were not passed in this call.
 */
export const setChatbotContext = (context: ChatbotTrackingContext): void => {
  chatbotContext = {
    ...chatbotContext,
    ...omitUndefined(context),
  };

  if (chatbotContext.brand) {
    setTag('brand', chatbotContext.brand);
  }
  if (chatbotContext.route) {
    setTag('route', chatbotContext.route);
  }
  if (chatbotContext.flow) {
    setTag('flow', chatbotContext.flow);
  }
  if (typeof chatbotContext.isGuest === 'boolean') {
    setTag('is_guest', String(chatbotContext.isGuest));
  }
  if (chatbotContext.clientId) {
    setTag('client_id', chatbotContext.clientId);
  }
  if (chatbotContext.widgetId) {
    setTag('widget_id', chatbotContext.widgetId);
  }

  // Replace Sentry context with the full merged snapshot (defined keys only).
  setContext('chatbot', omitUndefined(chatbotContext));
};

/**
 * Prefer route name / pattern path so dynamic params (article ids) never leave the client as tags.
 */
export const resolveTrackingRoute = (route?: TrackingRouteLike | null): string | undefined => {
  if (!route) return undefined;
  if (route.name != null && String(route.name)) {
    return String(route.name);
  }
  const matched = route.matched;
  const matchedPath = matched?.length ? matched[matched.length - 1]?.path : undefined;
  if (matchedPath) return matchedPath;
  return route.path || undefined;
};

/**
 * Keep the `route` tag in sync with vue-router navigation.
 * Safe to call once after router creation (e.g. from main.ts).
 */
export const bindChatbotRouteTracking = (router: RouterLike): (() => void) => {
  const applyRoute = (route: TrackingRouteLike): void => {
    const resolved = resolveTrackingRoute(route);
    if (!resolved) return;
    setChatbotContext({ route: resolved });
  };

  applyRoute(router.currentRoute);
  return router.afterEach((to) => {
    applyRoute(to);
  });
};
